import { useEffect, useState } from "react";
import FormularioCredenciales, { OrigenCredenciales } from "../../components/EstatusFacturas/FormularioCredencialesAriba.tsx";
import BodyEstatusFacturas from "../../components/EstatusFacturas/BodyEstatusfactura.tsx";
import Alert from "../../components/ui/Alert.tsx";
import { getAuth } from "firebase/auth";
import { FileText, Send, Users, ShieldAlert } from "lucide-react";
import { useProject } from "../../context/ProjectContext.tsx";
import {
  obtenerConfigCliente,
  guardarConfigCliente,
  obtenerEstadoCodigoProduccion,
  verificarCodigoProduccion,
  CigConexionConfig,
  SystemIds
} from "../../services/clienteConexionService.ts";

const SYSTEM_IDS_VACIOS: SystemIds = { dev: "", qas: "", prd: "" };

const Statusfactura = ({ setPage }: { setPage: (page: string) => void }) => {
  const { project } = useProject();
  const clienteId = project?.clienteId || "";

  // --- ESTADOS ---
  const [origenCredenciales, setOrigenCredenciales] = useState<OrigenCredenciales>("manual");
  const [tieneCredencialesGuardadas, setTieneCredencialesGuardadas] = useState(false);
  const [usuarioGuardado, setUsuarioGuardado] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [guardarCredenciales, setGuardarCredenciales] = useState(false);
  const [lifnr, setLifnr] = useState("");
  const [systemid, setSystemId] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [selectedMethod, setSelectedMethod] = useState<"LIFNR" | "Custom">("LIFNR");
  const [selectedRequest, setSelectedRequest] = useState("");
  const [documentReferenceId, setDocumentReferenceId] = useState("");
  const [invoiceStatusDesc, setInvoiceStatusDesc] = useState("");
  const [comments, setComments] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const [ambiente, setAmbiente] = useState("");
  const [dataCenter, setDataCenter] = useState("");
  const [erpType, setErpType] = useState<"onprem" | "cloud" | "">("");
  const [systemIds, setSystemIds] = useState<SystemIds>(SYSTEM_IDS_VACIOS);
  const [enviando, setEnviando] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [prodCode, setProdCode] = useState("");
  const [isProdConfirmed, setIsProdConfirmed] = useState(false);
  const [verificandoCodigo, setVerificandoCodigo] = useState(false);
  const [tieneCodigoConfigurado, setTieneCodigoConfigurado] = useState<boolean | null>(null);

  // --- LÓGICA ---
  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 5000);
  };

  // Precarga Data Center / Tipo de ERP / System IDs guardados para este
  // cliente (ver Configuración), y si hay credenciales guardadas, las
  // ofrece como origen por defecto.
  useEffect(() => {
    if (!clienteId) return;
    obtenerConfigCliente<CigConexionConfig>(clienteId, "cig")
      .then((config) => {
        setDataCenter((prev) => prev || config.dataCenter);
        setErpType((prev) => prev || config.erpType);
        setSystemIds(config.systemIds);
        setTieneCredencialesGuardadas(config.tieneCredencialesGuardadas);
        setUsuarioGuardado(config.usuario);
        setOrigenCredenciales(config.tieneCredencialesGuardadas ? "guardadas" : "manual");
      })
      .catch((err) => console.error("Error cargando configuración de conexión:", err));
    obtenerEstadoCodigoProduccion(clienteId)
      .then((estado) => setTieneCodigoConfigurado(estado.tieneCodigoGuardado))
      .catch((err) => console.error("Error consultando código de producción:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const handleAmbienteChange = (value: string) => {
    setAmbiente(value);
    if (value === "prd") {
      setIsProdConfirmed(false);
      setShowModal(true);
    } else {
      setIsProdConfirmed(false);
    }
  };

  const handleConfirmProd = async () => {
    setVerificandoCodigo(true);
    try {
      const valido = await verificarCodigoProduccion(clienteId, prodCode);
      if (valido) {
        setIsProdConfirmed(true);
        setShowModal(false);
        showAlert("✅ Acceso a producción confirmado.", "success");
      } else {
        showAlert("❌ Código incorrecto. Inténtalo de nuevo.", "error");
      }
    } catch (err: any) {
      showAlert(err.message || "No se pudo validar el código.", "error");
    } finally {
      setVerificandoCodigo(false);
    }
  };

  const handleCancelModal = () => {
    setShowModal(false);
    setAmbiente("");
    setIsProdConfirmed(false);
    setProdCode("");
  };

  // --- TEMPLATES SOAP ---
  const soapTemplates = {
    onprem: () => `
    <soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
      <soap:Body>
        <n0:InvoiceStatusUpdateRequest xmlns:n0="http://sap.com/xi/ARBCIG1">
          <item>
            ${comments ? `<Comments><Comment LANG="es">${comments}</Comment></Comments>` : ""}
            <LOGICAL_SYSTEM>${systemid}</LOGICAL_SYSTEM>
            <LIFNR>${lifnr}</LIFNR>
            <INV_DOC_NO>${documentReferenceId}</INV_DOC_NO>
            <DOC_DATE>${new Date().toISOString().split('T')[0]}</DOC_DATE>
            <DOC_TIME>${new Date().toLocaleTimeString('it-IT')}</DOC_TIME>
            <INVOICE_STATUS>${invoiceStatusDesc}</INVOICE_STATUS>
            <DUE_DATE>${new Date().toISOString().split('T')[0]}</DUE_DATE>
            <AMT_DOCCUR>0.0</AMT_DOCCUR>
            <CURRENCY>PEN</CURRENCY>
          </item>
        </n0:InvoiceStatusUpdateRequest>
      </soap:Body>
    </soap:Envelope>`,
    cloud: () => `
    <soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
      <soap:Header/>
      <soap:Body>
        <ns1:InvoiceStatusUpdateNotification xmlns:ns1="http://sap.com/xi/Procurement">
          <MessageHeader>
            <ID>${Date.now()}</ID>
            <CreationDateTime>${new Date().toISOString()}</CreationDateTime>
            <SenderBusinessSystemID>${systemid}</SenderBusinessSystemID>
            <RecipientParty>
              <InternalID schemeID="${lifnr}">${lifnr}</InternalID>
            </RecipientParty>
          </MessageHeader>
          <InvoiceStatus>
            <SupplierInvoice>${documentReferenceId}</SupplierInvoice>
            <FiscalYear>${new Date().getFullYear()}</FiscalYear>
            <DocumentReferenceID>${documentReferenceId}</DocumentReferenceID>
            <SupplierInvoiceStatusDesc>${invoiceStatusDesc}</SupplierInvoiceStatusDesc>
            ${comments ? `<Comments>${comments}</Comments>` : ""}
          </InvoiceStatus>
        </ns1:InvoiceStatusUpdateNotification>
      </soap:Body>
    </soap:Envelope>`
  };

  const sendSoapRequest = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      showAlert("No se detectó una sesión activa. Por favor, reingresa al sistema.", "error");
      return;
    }
    if (!clienteId) {
      showAlert("Este proyecto no tiene un cliente asociado.", "error");
      return;
    }

    // Validaciones de negocio
    if (ambiente === "prd" && !isProdConfirmed) {
      showAlert("⚠️ Debes confirmar el código de producción antes de continuar.", "warning");
      setShowModal(true);
      return;
    }
    if (origenCredenciales === "manual" && (!username || !password)) {
      showAlert("Por favor, ingresa usuario y contraseña.", "error");
      return;
    }
    if (!dataCenter || !erpType || !ambiente) {
      showAlert("Por favor, completa la configuración de conexión.", "error");
      return;
    }

    if (selectedMethod === "LIFNR") {
      if (!lifnr.trim() || !documentReferenceId.trim() || !invoiceStatusDesc.trim() || !systemid.trim()) {
        showAlert("Por favor, completa todos los campos de la factura.", "error");
        return;
      }
    } else if (!customBody.trim()) {
      showAlert("Por favor, ingresa el cuerpo XML manual.", "error");
      return;
    }

    setEnviando(true);
    try {
      if (origenCredenciales === "manual" && guardarCredenciales) {
        await guardarConfigCliente(clienteId, "cig", {
          dataCenter,
          erpType,
          systemIds,
          usuario: username,
          contraseña: password
        });
      }

      const token = await user.getIdToken();
      const tipo = origenCredenciales === "guardadas" ? 1 : 2;
      const credenciales = origenCredenciales === "guardadas" ? clienteId : `${username}:${password}`;

      const baseUrl = dataCenter === "eu" ? "managedgateway.cloud.sap" : "managedgateway.cloud.sap"; // Ajustar si cambia el subdominio
      const ambientePrefix = ambiente === "test" ? "test-integration" : "integration";
      const ambienteUrl = `https://${ambientePrefix}.${dataCenter}.${baseUrl}:443/cxf/receiveSOAP`;

      let soapRequest = selectedMethod === "Custom" ? customBody : (erpType === "onprem" ? soapTemplates.onprem() : soapTemplates.cloud());

      const response = await fetch(`${process.env.REACT_APP_API_URL}/estatusfact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          tipo,
          credencials: credenciales,
          app: "cig",
          ambiente: ambienteUrl,
          body: soapRequest,
        }),
      });

      const responseText = await response.text();
      if (response.ok) {
        showAlert("Solicitud enviada correctamente.", "success");
      } else {
        showAlert(`Error: ${responseText}`, "error");
      }
    } catch (error: any) {
      showAlert(error.message || "Hubo un error al conectar con el servidor.", "error");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <main className="min-h-screen p-4 md:p-8 bg-transparent text-white">
      {/* HEADER & BREADCRUMBS */}
      <header className="max-w-7xl mx-auto mb-10">
        <h1 className="text-4xl font-extrabold mt-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
          SAP CIG <span className="text-white/20 font-light">|</span> Web Service
        </h1>
        <p className="text-gray-500 mt-2 font-light">Gestión de estados de facturación vía SOAP Proxy</p>
      </header>

      <div className="max-w-7xl mx-auto">
        <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

          {/* COLUMNA IZQUIERDA: CONFIGURACIÓN */}
          <section className="lg:col-span-5 bg-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl">
            <div className="flex items-center gap-3 mb-8 border-b border-white/5 pb-4">
              <div className="p-3 bg-blue-500/20 rounded-2xl text-blue-400"><Users size={24} /></div>
              <h2 className="text-xl font-bold">Conexión</h2>
            </div>
            <FormularioCredenciales
              origenCredenciales={origenCredenciales} setOrigenCredenciales={setOrigenCredenciales}
              tieneCredencialesGuardadas={tieneCredencialesGuardadas} usuarioGuardado={usuarioGuardado}
              username={username} password={password} setUsername={setUsername} setPassword={setPassword}
              guardarCredenciales={guardarCredenciales} setGuardarCredenciales={setGuardarCredenciales}
              ambiente={ambiente} setAmbiente={handleAmbienteChange}
              dataCenter={dataCenter} setDataCenter={setDataCenter} erpType={erpType} setErpType={setErpType}
              systemIds={systemIds} setSystemId={setSystemId}
            />
          </section>

          {/* COLUMNA DERECHA: DATOS/BODY */}
          <section className="lg:col-span-7 space-y-6">
            <div className="bg-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl">
              <div className="flex items-center gap-3 mb-8 border-b border-white/5 pb-4">
                <div className="p-3 bg-indigo-500/20 rounded-2xl text-indigo-400"><FileText size={24} /></div>
                <h2 className="text-xl font-bold">Datos del Mensaje</h2>
              </div>
              <BodyEstatusFacturas
                lifnr={lifnr} setLifnr={setLifnr} customBody={customBody} setCustomBody={setCustomBody}
                selectedMethod={selectedMethod} setSelectedMethod={setSelectedMethod}
                documentReferenceId={documentReferenceId} setDocumentReferenceId={setDocumentReferenceId}
                invoiceStatusDesc={invoiceStatusDesc} setInvoiceStatusDesc={setInvoiceStatusDesc}
                comments={comments} setComments={setComments} systemid={systemid} setSystemId={setSystemId}
                selectedRequest={selectedRequest} setSelectedRequest={setSelectedRequest}
              />
            </div>

            {/* BOTÓN DE ACCIÓN */}
            <button
              onClick={sendSoapRequest}
              disabled={enviando}
              className="group relative w-full py-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 rounded-[1.5rem] font-bold text-lg shadow-xl shadow-blue-900/20 transition-all transform active:scale-[0.98] flex items-center justify-center gap-3 overflow-hidden"
            >
              <Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              {enviando ? "Enviando..." : "Enviar a SAP Business Network"}
            </button>
          </section>
        </div>
      </div>

      {/* MODAL DE SEGURIDAD PARA PRODUCCIÓN */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fadeIn">
          <div className="bg-[rgb(var(--c-bg))] border border-white/10 p-10 rounded-[2.5rem] max-w-md w-full shadow-2xl">
            <div className="flex flex-col items-center text-center">
              <div className="p-4 bg-red-500/20 rounded-full text-red-500 mb-6">
                <ShieldAlert size={48} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Confirmar Producción</h3>
              <p className="text-gray-400 mb-8 text-sm">Estás a punto de enviar datos reales al entorno de producción de SAP Ariba.</p>

              {tieneCodigoConfigurado === false ? (
                <p className="w-full bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-2xl p-4 text-xs mb-8 text-left">
                  Este cliente todavía no tiene un código de confirmación configurado. Pide a un administrador que lo configure en Configuración → CIG antes de enviar a Producción.
                </p>
              ) : (
                <input
                  type="password"
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-center text-3xl tracking-[0.5em] mb-8 outline-none focus:border-red-500 transition-colors"
                  value={prodCode}
                  onChange={(e) => setProdCode(e.target.value)}
                  placeholder="****"
                  disabled={verificandoCodigo}
                  onKeyDown={(e) => e.key === "Enter" && !verificandoCodigo && handleConfirmProd()}
                />
              )}

              <div className="flex gap-4 w-full">
                <button
                  className="flex-1 py-4 bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded-2xl font-bold transition-colors"
                  onClick={handleConfirmProd}
                  disabled={tieneCodigoConfigurado === false || verificandoCodigo}
                >
                  {verificandoCodigo ? "Verificando..." : "Confirmar"}
                </button>
                <button className="flex-1 py-4 bg-white/5 hover:bg-white/10 rounded-2xl font-bold transition-colors" onClick={handleCancelModal}>Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Statusfactura;
